import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatCurrency as formatCurrencyLib, getCurrencySymbol } from '../lib/currency';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Download, TrendingUp, TrendingDown, DollarSign, FileText } from 'lucide-react';

const COLORS = ['#0d9488', '#f59e0b', '#ef4444', '#8b5cf6', '#3b82f6', '#ec4899', '#10b981', '#6366f1'];

const Reports = () => {
  const { company } = useAuth();
  const currency = company?.currency || 'USD';
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  const [pnlData, setPnlData] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [loanSummary, setLoanSummary] = useState(null);
  const [taxSummary, setTaxSummary] = useState(null);

  // Helper to format currency using company default
  const formatCurrency = (amount) => {
    return formatCurrencyLib(amount || 0, currency);
  };

  // Helper for chart axis formatting
  const formatChartValue = (value) => {
    const symbol = getCurrencySymbol(currency);
    if (value >= 1000000) return `${symbol}${(value/1000000).toFixed(1)}M`;
    if (value >= 1000) return `${symbol}${(value/1000).toFixed(0)}k`;
    return `${symbol}${value}`;
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const [pnlRes, bsRes, cfRes, loanRes, taxRes] = await Promise.all([
        api.get(`/reports/profit-loss?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`),
        api.get(`/reports/balance-sheet?as_of_date=${dateRange.end_date}`),
        api.get(`/reports/cash-flow?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`),
        api.get('/reports/loan-summary'),
        api.get('/reports/tax-summary')
      ]);
      setPnlData(pnlRes.data);
      setBalanceSheet(bsRes.data);
      setCashFlow(cfRes.data);
      setLoanSummary(loanRes.data);
      setTaxSummary(taxRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const exportToCsv = async (type) => {
    try {
      let endpoint = '';
      switch (type) {
        case 'transactions':
          endpoint = `/reports/export/transactions?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
          break;
        case 'profit-loss':
          endpoint = `/reports/export/profit-loss?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
          break;
        case 'balance-sheet':
          endpoint = `/reports/export/balance-sheet?as_of_date=${dateRange.end_date}`;
          break;
        case 'loans':
          endpoint = '/reports/export/loans';
          break;
        case 'receivables':
          endpoint = '/reports/export/receivables';
          break;
        case 'payables':
          endpoint = '/reports/export/payables';
          break;
        default:
          return;
      }
      
      const response = await api.get(endpoint, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${type.replace('-', ' ')} exported successfully!`);
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            Reports
          </h1>
          <p className="text-[hsl(var(--muted-ink))] mt-1">
            Financial statements and summaries
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm">From</Label>
            <Input
              type="date"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
              className="w-[140px]"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">To</Label>
            <Input
              type="date"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
              className="w-[140px]"
            />
          </div>
        </div>
      </div>

      <Tabs defaultValue="pnl">
        <TabsList className="mb-6">
          <TabsTrigger value="pnl">Profit & Loss</TabsTrigger>
          <TabsTrigger value="balance">Balance Sheet</TabsTrigger>
          <TabsTrigger value="cashflow">Cash Flow</TabsTrigger>
          <TabsTrigger value="loans">Loans</TabsTrigger>
          <TabsTrigger value="taxes">Taxes</TabsTrigger>
        </TabsList>

        {/* Profit & Loss */}
        <TabsContent value="pnl">
          {loading ? (
            <Card><CardContent className="p-8"><div className="animate-pulse h-64 bg-gray-100 rounded" /></CardContent></Card>
          ) : pnlData && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-green-600 text-sm mb-1">
                      <TrendingUp size={14} />
                      Total Revenue
                    </div>
                    <p className="text-2xl font-semibold numbered text-green-600">
                      {formatCurrency(pnlData.revenue.total)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-red-600 text-sm mb-1">
                      <TrendingDown size={14} />
                      Total Expenses
                    </div>
                    <p className="text-2xl font-semibold numbered text-red-600">
                      {formatCurrency(pnlData.expenses.total)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-[hsl(var(--muted-ink))] text-sm mb-1">
                      <DollarSign size={14} />
                      Net Profit
                    </div>
                    <p className={`text-2xl font-semibold numbered ${pnlData.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(pnlData.net_profit)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-[hsl(var(--muted-ink))] text-sm mb-1">
                      Profit Margin
                    </div>
                    <p className={`text-2xl font-semibold ${pnlData.profit_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {pnlData.profit_margin}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts and Details */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Revenue by Category */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Revenue by Category</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pnlData.revenue.by_category}
                            dataKey="amount"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {pnlData.revenue.by_category.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Expenses by Category */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Expenses by Category</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={pnlData.expenses.by_category} layout="vertical">
                          <XAxis type="number" tickFormatter={formatChartValue} />
                          <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Bar dataKey="amount" fill="#ef4444" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Table */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Detailed Report</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => exportToCsv('profit-loss')} data-testid="export-pnl-btn">
                    <Download size={16} className="mr-2" />
                    Export CSV
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Revenue Section */}
                    <div>
                      <h3 className="font-semibold text-green-600 mb-2 flex items-center gap-2">
                        <TrendingUp size={16} />
                        Revenue
                      </h3>
                      <table className="w-full text-sm">
                        <tbody>
                          {pnlData.revenue.by_category.map((item, index) => (
                            <tr key={index} className="border-b" data-testid="report-row-revenue">
                              <td className="py-2 pl-4">{item.name}</td>
                              <td className="py-2 text-right numbered">{formatCurrency(item.amount)}</td>
                            </tr>
                          ))}
                          <tr className="font-semibold bg-green-50">
                            <td className="py-2 pl-4">Total Revenue</td>
                            <td className="py-2 text-right numbered">{formatCurrency(pnlData.revenue.total)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Expenses Section */}
                    <div>
                      <h3 className="font-semibold text-red-600 mb-2 flex items-center gap-2">
                        <TrendingDown size={16} />
                        Expenses
                      </h3>
                      <table className="w-full text-sm">
                        <tbody>
                          {pnlData.expenses.by_category.map((item, index) => (
                            <tr key={index} className="border-b" data-testid="report-row-expense">
                              <td className="py-2 pl-4">{item.name}</td>
                              <td className="py-2 text-right numbered">{formatCurrency(item.amount)}</td>
                            </tr>
                          ))}
                          <tr className="font-semibold bg-red-50">
                            <td className="py-2 pl-4">Total Expenses</td>
                            <td className="py-2 text-right numbered">{formatCurrency(pnlData.expenses.total)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Net */}
                    <div className={`p-4 rounded-lg ${pnlData.net_profit >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-lg">Net Profit / (Loss)</span>
                        <span className={`font-bold text-xl numbered ${pnlData.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {formatCurrency(pnlData.net_profit)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Balance Sheet */}
        <TabsContent value="balance">
          {loading ? (
            <Card><CardContent className="p-8"><div className="animate-pulse h-64 bg-gray-100 rounded" /></CardContent></Card>
          ) : balanceSheet && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Balance Sheet</CardTitle>
                  <p className="text-sm text-[hsl(var(--muted-ink))]">As of {formatDate(balanceSheet.as_of_date)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => exportToCsv('balance-sheet')} data-testid="export-bs-btn">
                    <Download size={16} className="mr-2" />
                    Export CSV
                  </Button>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    balanceSheet.is_balanced ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {balanceSheet.is_balanced ? 'Balanced ✓' : 'Not Balanced'}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Assets */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3 text-[hsl(var(--brand-600))]">Assets</h3>
                    <table className="w-full text-sm">
                      <tbody>
                        {balanceSheet.assets.items.map((item, index) => (
                          <tr key={index} className="border-b">
                            <td className="py-2">
                              <span className="text-[hsl(var(--muted-ink))] text-xs mr-2">{item.code}</span>
                              {item.name}
                            </td>
                            <td className="py-2 text-right numbered">{formatCurrency(item.balance)}</td>
                          </tr>
                        ))}
                        <tr className="font-semibold bg-[hsl(var(--brand-50))]">
                          <td className="py-2">Total Assets</td>
                          <td className="py-2 text-right numbered">{formatCurrency(balanceSheet.assets.total)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Liabilities + Equity */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3 text-amber-600">Liabilities</h3>
                    <table className="w-full text-sm mb-6">
                      <tbody>
                        {balanceSheet.liabilities.items.map((item, index) => (
                          <tr key={index} className="border-b">
                            <td className="py-2">
                              <span className="text-[hsl(var(--muted-ink))] text-xs mr-2">{item.code}</span>
                              {item.name}
                            </td>
                            <td className="py-2 text-right numbered">{formatCurrency(item.balance)}</td>
                          </tr>
                        ))}
                        <tr className="font-semibold bg-amber-50">
                          <td className="py-2">Total Liabilities</td>
                          <td className="py-2 text-right numbered">{formatCurrency(balanceSheet.liabilities.total)}</td>
                        </tr>
                      </tbody>
                    </table>

                    <h3 className="font-semibold text-lg mb-3 text-purple-600">Equity</h3>
                    <table className="w-full text-sm">
                      <tbody>
                        {balanceSheet.equity.items.map((item, index) => (
                          <tr key={index} className="border-b">
                            <td className="py-2">{item.name}</td>
                            <td className="py-2 text-right numbered">{formatCurrency(item.balance)}</td>
                          </tr>
                        ))}
                        <tr className="font-semibold bg-purple-50">
                          <td className="py-2">Total Equity</td>
                          <td className="py-2 text-right numbered">{formatCurrency(balanceSheet.equity.total)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Cash Flow */}
        <TabsContent value="cashflow">
          {loading ? (
            <Card><CardContent className="p-8"><div className="animate-pulse h-64 bg-gray-100 rounded" /></CardContent></Card>
          ) : cashFlow && (
            <Card>
              <CardHeader>
                <CardTitle>Cash Flow Statement</CardTitle>
                <p className="text-sm text-[hsl(var(--muted-ink))]">
                  {formatDate(cashFlow.period.start_date)} to {formatDate(cashFlow.period.end_date)}
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Opening */}
                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                    <span className="font-medium">Opening Cash Balance</span>
                    <span className="text-xl font-semibold numbered">{formatCurrency(cashFlow.opening_cash)}</span>
                  </div>

                  {/* Operating */}
                  <div>
                    <h3 className="font-semibold mb-3">Operating Activities</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-600 mb-1">Cash Inflows</p>
                        <p className="text-xl font-semibold numbered text-green-700">{formatCurrency(cashFlow.operating.inflows)}</p>
                      </div>
                      <div className="p-4 bg-red-50 rounded-lg">
                        <p className="text-sm text-red-600 mb-1">Cash Outflows</p>
                        <p className="text-xl font-semibold numbered text-red-700">{formatCurrency(cashFlow.operating.outflows)}</p>
                      </div>
                      <div className={`p-4 rounded-lg ${cashFlow.operating.net >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                        <p className="text-sm text-[hsl(var(--muted-ink))] mb-1">Net Operating</p>
                        <p className={`text-xl font-semibold numbered ${cashFlow.operating.net >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                          {formatCurrency(cashFlow.operating.net)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Financing */}
                  <div>
                    <h3 className="font-semibold mb-3">Financing Activities</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-600 mb-1">Cash Inflows</p>
                        <p className="text-xl font-semibold numbered text-green-700">{formatCurrency(cashFlow.financing.inflows)}</p>
                      </div>
                      <div className="p-4 bg-red-50 rounded-lg">
                        <p className="text-sm text-red-600 mb-1">Cash Outflows</p>
                        <p className="text-xl font-semibold numbered text-red-700">{formatCurrency(cashFlow.financing.outflows)}</p>
                      </div>
                      <div className={`p-4 rounded-lg ${cashFlow.financing.net >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                        <p className="text-sm text-[hsl(var(--muted-ink))] mb-1">Net Financing</p>
                        <p className={`text-xl font-semibold numbered ${cashFlow.financing.net >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                          {formatCurrency(cashFlow.financing.net)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Net Change */}
                  <div className={`flex justify-between items-center p-4 rounded-lg ${
                    cashFlow.net_cash_flow >= 0 ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <span className="font-medium">Net Change in Cash</span>
                    <span className={`text-xl font-semibold numbered ${cashFlow.net_cash_flow >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(cashFlow.net_cash_flow)}
                    </span>
                  </div>

                  {/* Closing */}
                  <div className="flex justify-between items-center p-4 bg-[hsl(var(--brand-50))] rounded-lg">
                    <span className="font-bold">Closing Cash Balance</span>
                    <span className="text-2xl font-bold numbered text-[hsl(var(--brand-700))]">{formatCurrency(cashFlow.closing_cash)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Loans Summary */}
        <TabsContent value="loans">
          {loading ? (
            <Card><CardContent className="p-8"><div className="animate-pulse h-64 bg-gray-100 rounded" /></CardContent></Card>
          ) : loanSummary && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Loan Summary</CardTitle>
                <Button variant="outline" size="sm" onClick={() => exportToCsv('loans')} data-testid="export-loans-btn">
                  <Download size={16} className="mr-2" />
                  Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-[hsl(var(--muted-ink))]">Total Loans</p>
                    <p className="text-2xl font-semibold">{loanSummary.summary.total_loans}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-[hsl(var(--muted-ink))]">Active</p>
                    <p className="text-2xl font-semibold">{loanSummary.summary.active_loans}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-[hsl(var(--muted-ink))]">Total Principal</p>
                    <p className="text-2xl font-semibold numbered">{formatCurrency(loanSummary.summary.total_principal)}</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-600">Outstanding</p>
                    <p className="text-2xl font-semibold numbered text-red-600">{formatCurrency(loanSummary.summary.total_outstanding)}</p>
                  </div>
                </div>

                {/* Loan Table */}
                {loanSummary.loans.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3">Loan</th>
                        <th className="text-left py-3">Lender</th>
                        <th className="text-right py-3">Principal</th>
                        <th className="text-right py-3">Rate</th>
                        <th className="text-right py-3">Balance</th>
                        <th className="text-right py-3">Interest Paid</th>
                        <th className="text-left py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loanSummary.loans.map((loan, index) => (
                        <tr key={index} className="border-b">
                          <td className="py-3">{loan.loan_number}</td>
                          <td className="py-3">{loan.lender}</td>
                          <td className="py-3 text-right numbered">{formatCurrency(loan.principal)}</td>
                          <td className="py-3 text-right">{loan.interest_rate}%</td>
                          <td className="py-3 text-right numbered text-red-600">{formatCurrency(loan.balance)}</td>
                          <td className="py-3 text-right numbered text-amber-600">{formatCurrency(loan.interest_paid)}</td>
                          <td className="py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              loan.status === 'active' ? 'bg-green-100 text-green-700' :
                              loan.status === 'paid_off' ? 'bg-gray-100 text-gray-600' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {loan.status.replace('_', ' ')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-8 text-[hsl(var(--muted-ink))]">
                    No loans found
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tax Summary */}
        <TabsContent value="taxes">
          {loading ? (
            <Card><CardContent className="p-8"><div className="animate-pulse h-64 bg-gray-100 rounded" /></CardContent></Card>
          ) : taxSummary && (
            <Card>
              <CardHeader>
                <CardTitle>Tax Summary - {taxSummary.year}</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-[hsl(var(--muted-ink))]">Total Due</p>
                    <p className="text-2xl font-semibold numbered">{formatCurrency(taxSummary.summary.total_due)}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600">Total Paid</p>
                    <p className="text-2xl font-semibold numbered text-green-600">{formatCurrency(taxSummary.summary.total_paid)}</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-600">Outstanding</p>
                    <p className="text-2xl font-semibold numbered text-red-600">{formatCurrency(taxSummary.summary.outstanding)}</p>
                  </div>
                </div>

                {/* By Tax Type */}
                {taxSummary.by_tax_type.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3">Tax Type</th>
                        <th className="text-right py-3">Entries</th>
                        <th className="text-right py-3">Total Due</th>
                        <th className="text-right py-3">Total Paid</th>
                        <th className="text-right py-3">Outstanding</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taxSummary.by_tax_type.map((item, index) => (
                        <tr key={index} className="border-b">
                          <td className="py-3 font-medium">{item.name}</td>
                          <td className="py-3 text-right">{item.entry_count}</td>
                          <td className="py-3 text-right numbered">{formatCurrency(item.total_due)}</td>
                          <td className="py-3 text-right numbered text-green-600">{formatCurrency(item.total_paid)}</td>
                          <td className="py-3 text-right numbered text-red-600">{formatCurrency(item.outstanding)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-8 text-[hsl(var(--muted-ink))]">
                    No tax entries found
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
